import { useGetMyProfileQuery } from "@/features/dashboard/api/dashboardApi";
import { useHasPermission } from "@/shared/auth/usePermissions";
import { ErrorState } from "@/shared/components/ErrorState";
import { formatDate, formatDateTime } from "@/shared/lib/datetime";
import { Permission } from "@keystone/contracts/shared";
import { Badge, Card, Group, Skeleton, Stack, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { FiUser } from "react-icons/fi";

export function MyProfileWidget() {
  const { t, i18n } = useTranslation("dashboard");
  const { data: profile, isLoading, error, refetch } = useGetMyProfileQuery();
  const canViewRoles = useHasPermission(Permission.IsInternalUsers);

  return (
    <Card withBorder padding="lg" h="100%">
      <Stack gap="sm">
        <Group gap="xs">
          <FiUser aria-hidden="true" />
          <Title order={4}>{t("widgets.profile.title")}</Title>
        </Group>

        {isLoading ? (
          <Stack gap="xs">
            <Skeleton height={12} width="60%" />
            <Skeleton height={12} width="80%" />
            <Skeleton height={12} width="50%" />
          </Stack>
        ) : error || !profile ? (
          <ErrorState message={t("widgets.profile.error")} onRetry={() => void refetch()} />
        ) : (
          <Stack gap="sm">
            <ProfileField label={t("widgets.profile.fields.email")} value={profile.email} />
            <ProfileField
              label={t("widgets.profile.fields.lastLogin")}
              value={
                profile.lastLogin
                  ? formatDateTime(profile.lastLogin, i18n.language)
                  : t("widgets.profile.lastLoginNever")
              }
            />
            <ProfileField
              label={t("widgets.profile.fields.createdAt")}
              value={formatDate(profile.createdAt, i18n.language)}
            />
            {canViewRoles && (
              <Stack gap={4}>
                <Text size="xs" c="dimmed" tt="uppercase">
                  {t("widgets.profile.fields.roles")}
                </Text>
                {profile.roles.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    {t("widgets.profile.noRoles")}
                  </Text>
                ) : (
                  <Group gap="xs">
                    {profile.roles.map((role) => (
                      <Badge
                        key={role}
                        variant="light"
                        color={role === "Administrator" ? "grape" : "blue"}
                      >
                        {role}
                      </Badge>
                    ))}
                  </Group>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

interface ProfileFieldProps {
  label: string;
  value: string;
  mono?: boolean;
}

function ProfileField({ label, value, mono }: ProfileFieldProps) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text
        size="sm"
        {...(mono ? { ff: "monospace" as const } : {})}
        style={{ wordBreak: "break-all" }}
      >
        {value}
      </Text>
    </Stack>
  );
}
